import { ApiError, friendlyErrorCopy, isConnectivityError, userMessage } from '../api/client';
import { StepUpCancelledError, StepUpUnavailableError } from '../api/stepUp';

/**
 * Default-deny error copy for one write surface (crossmarc/steward-app#1605).
 *
 * `userMessage(err, fallback)` echoes an `ApiError`'s `message` for any code it does not curate,
 * and the `|| fallback` after it only catches an EMPTY message. For a write the façade forwards
 * from appcore, that message can be appcore's own raw text (`throwUpstream`, `errorToResponse`),
 * and several appcore default codes collide with codes the façade raises itself, so a code
 * allowlist alone cannot tell the two apart. This fails closed instead: an `ApiError` is shown
 * only what the surface's own map, or the app-wide `FRIENDLY_ERROR_CODE` table, says for it, and
 * anything else logs the real error to the console and shows the surface's fixed fallback. The
 * server's `message` is never rendered.
 *
 * `copy` keys, tried in this order (first own-property hit wins):
 *   `reason:<reason>`   a 409 `CONFLICT` whose façade-set `reason` names a cause. Read before the
 *                       code, because a `CONFLICT`'s code says nothing about which conflict.
 *   `<status>:<code>`   e.g. `403:FORBIDDEN`
 *   `<code>`            e.g. `ROLE_NOT_ASSIGNABLE`
 *   `<status>`          e.g. `403`, for a surface whose whole status has one meaning
 *
 * A step-up 403 is deliberately not special-cased: `api/client.ts` retries it itself and only
 * rethrows once those attempts are spent. It gets the fallback PROVIDED no map keys the bare
 * status `403`; a surface that wants a step-up 403 to fall back must key `403:<code>`, as
 * `SelectWorkspace.tsx`'s two maps do (pinned there by test). A bare-status key would catch it.
 *
 * A non-`ApiError` gets the surface fallback, logged, unless it is one of three recognised cases
 * (crossmarc/steward-app#2040). The first two are thrown classes whose copy this SPA wrote, and
 * pass through untouched; the third is a predicate, and its errors are replaced:
 *   `StepUpCancelledError`   a dismissed step-up prompt ("Step-up verification was cancelled.");
 *   `StepUpUnavailableError` no step-up controller mounted (`api/stepUp.ts`);
 *   `isConnectivityError`    (predicate, not a class) true for ANY `TypeError`, a `DOMException` `AbortError` or
 *                            `TimeoutError`. That covers offline and timeout, and also a
 *                            `TypeError` thrown by a bug, which then reads as "couldn't reach
 *                            Steward"; `userMessage` replaces all of them with its own written line.
 * Any other value, a `SyntaxError` from a 2xx with a non-JSON body and a bare `Error` included,
 * gets the fallback and its `message` is never rendered. The classes are matched by
 * `instanceof`, not by message text.
 *
 * `copy` is an `Object.hasOwn` lookup, so a hostile `code` such as `constructor` cannot reach an
 * inherited function.
 */
export function curatedErrorMessage(
  err: unknown,
  copy: Readonly<Record<string, string>>,
  fallback: string,
  surface: string,
): string {
  if (!(err instanceof ApiError)) {
    if (err instanceof StepUpCancelledError || err instanceof StepUpUnavailableError) return err.message;
    if (isConnectivityError(err)) return userMessage(err, fallback);
    console.error(`${surface}: unrecognized failure`, err);
    return fallback;
  }
  const keys: string[] = [];
  if (err.status === 409 && err.code === 'CONFLICT' && typeof err.reason === 'string') keys.push(`reason:${err.reason}`);
  keys.push(`${err.status}:${err.code}`, err.code, String(err.status));
  for (const key of keys) {
    if (Object.hasOwn(copy, key)) return copy[key];
  }
  const friendly = friendlyErrorCopy(err.code);
  if (friendly) return friendly;
  console.error(`${surface}: unrecognized failure`, { status: err.status, code: err.code, message: err.message });
  return fallback;
}
